import React, { useState, useEffect, useCallback } from 'react';
// Assuming you have this CSS file in the same directory:
// import './InfiniteTypingEffect.css'; 

// --- 1. Define Props Interface ---

interface InfiniteTypingEffectProps {
    /** The array of strings to cycle through and display. */
    textArray: string[];
    /** Time in milliseconds per character for typing. Default: 100 */
    typingSpeedMs?: number;
    /** Time in milliseconds per character for erasing. Default: 50 */
    erasingSpeedMs?: number;
    /** Pause duration in milliseconds after typing before erasing. Default: 1000 */
    pauseDurationMs?: number;
}

// --- 2. Configuration (using default values from props) ---

const DEFAULT_TYPING_SPEED_MS = 100;
const DEFAULT_ERASING_SPEED_MS = 100;
const DEFAULT_PAUSE_DURATION_MS = 2000;


/**
 * An infinite typing effect component that cycles through an array of strings.
 * The animation is driven by dynamically updating CSS styles based on text length.
 */
const InfiniteTypingEffect: React.FC<InfiniteTypingEffectProps> = ({ 
    textArray,
    typingSpeedMs = DEFAULT_TYPING_SPEED_MS,
    erasingSpeedMs = DEFAULT_ERASING_SPEED_MS,
    pauseDurationMs = DEFAULT_PAUSE_DURATION_MS,
}) => {
    // State is strictly typed by TypeScript inference based on the initial value
    const [typedText, setTypedText] = useState<string>('');
    const [animationStyle, setAnimationStyle] = useState<string>('');
    const [currentTextIndex, setCurrentTextIndex] = useState<number>(0);

    // Utility function to wait (returns a Promise<void>)
    const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

    // Clear and reset the animation style
    const clearAnimation = useCallback((): void => {
        // Only the static blink-cursor animation should remain
        setAnimationStyle(`blink-cursor 0.75s step-end infinite`);
    }, []);


    // 1. Typing Logic
    const typeText = useCallback(async (text: string): Promise<void> => {
        setTypedText(text);
        const length: number = text.length;
        const duration: number = (length * typingSpeedMs) / 1000; // Convert to seconds

        // Set CSS animation for typing
        const typingAnimation: string = 
            `typing ${duration}s steps(${length}, end) forwards, blink-cursor 0.75s step-end infinite`;
        
        setAnimationStyle(typingAnimation);
        
        // Wait for the animation to complete
        await delay(duration * 1000); 
    }, [typingSpeedMs]); // Dependency on typingSpeedMs

    // 2. Erasing Logic
    const eraseText = useCallback(async (text: string): Promise<void> => {
        const length: number = text.length;
        const duration: number = (length * erasingSpeedMs) / 1000; // Convert to seconds

        // Set CSS animation for erasing
        const erasingAnimation: string = 
            `erasing ${duration}s steps(${length}, end) forwards, blink-cursor 0.75s step-end infinite`;
        
        setAnimationStyle(erasingAnimation);

        // Wait for the animation to complete
        await delay(duration * 1000);
        
        // Clear the text content and reset animation style
        setTypedText('');
        clearAnimation();
    }, [erasingSpeedMs, clearAnimation]); // Dependency on erasingSpeedMs and clearAnimation

    // 3. Main Loop (useEffect)
    useEffect(() => {
        if (!textArray || textArray.length === 0) {
            setTypedText('No text array provided.');
            clearAnimation();
            return;
        }

        let isCancelled: boolean = false;
        
        const startTypingLoop = async (): Promise<void> => {
            while (!isCancelled) {
                const currentText: string = textArray[currentTextIndex];

                // Type
                await typeText(currentText);
                if (isCancelled) break;

                // Pause
                await delay(pauseDurationMs);
                if (isCancelled) break;

                // Erase (and pause longer on the last text before looping)
                const isLastText: boolean = currentTextIndex === textArray.length - 1;
                if (isLastText) {
                    await delay(pauseDurationMs * 2);
                }
                
                await eraseText(currentText);
                if (isCancelled) break;

                // Move to the next index, looping back to 0
                setCurrentTextIndex((prevIndex) => (prevIndex + 1) % textArray.length);
            }
        };
        
        // Start with a cleared animation style
        clearAnimation();
        startTypingLoop();

        // Cleanup function to stop the loop when the component unmounts
        return () => {
            isCancelled = true;
        };
        // Re-run when the index, text array, or speed configs change
    }, [currentTextIndex, textArray, typeText, eraseText, pauseDurationMs, clearAnimation]); 

    // The component structure
    return (
        <div className="typing-container">
            {/* The animation style is applied directly to the element */}
            <div 
                className="typed-text"
                style={{ animation: animationStyle }}
            >
                {typedText || '\u00A0' /* Non-breaking space when empty */}
            </div>
        </div>
    );
};

export default InfiniteTypingEffect;